import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchOrders, updateSingleOrder } from "../redux/Slice/orderSlice";
import { errorMessage } from "../services";
import { acceptOrder, addDemoOrders, exportOrdersCsv, getOrderById, rejectOrder, trackOrder, updateOrder } from "../services/orders";
import { selectUser } from "../redux/Slice/authSlice";
import { ordersChanged, onOrdersChanged } from "../utils/ordersChanged";
import ShipModal from "./orders/ShipModal";
import ReturnsPanel from "./orders/ReturnsPanel";
import { getSite } from "../services/sites";
import { dateText, money } from "../utils/format";
import { useDebounced } from "../utils/hooks";
import { toast as notify } from "../utils/toast";
import DataTable from "../components/DataTable";
import Pager from "../components/Pager";
import { AlertBell } from "../components/Alerts";
import "./manage-orders.css";
import Loader, { Spinner } from "../components/Loader";
import { FilterButton, FilterChips, FilterField, FilterSheet } from "../components/FilterSheet";

// A new order is Pending until it is accepted (Confirmed) or rejected (Cancelled)
const STATUSES = ["Pending", "Confirmed", "Processing", "Packed", "Shipped", "Out for delivery", "Delivered", "Cancelled", "Refunded"];
const PAYMENT_METHODS = ["GPay", "Card", "UPI", "Manual UPI", "Razorpay", "Cash on delivery", "COD"];
const PAYMENT_STATUS = { cod: "Pay on delivery", pending: "Awaiting payment", paid: "Paid", failed: "Failed" };
// A UPI order the shopper says is paid: the UPI reference (UTR) to find in the bank app before marking it paid
// UPI orders are paid through Razorpay, which confirms them, so they never need checking by hand. A demo payment
// (demo store or free trial, Ownezzy's test account) moved no real money and says so.
const isGatewayOrder = (o) => Boolean(o.gateway && o.gateway.provider);
const isDemoPayment = (o) => isGatewayOrder(o) && o.gateway.mode === "demo";
// Manual UPI: the shopper paid the store's UPI id and sent the order on WhatsApp; the owner checks it and marks it paid
const awaitingManualUpi = (o) => o.payment === "Manual UPI" && o.paymentStatus === "pending";

// WhatsApp the customer about their order: a chat with their number and a message from the store owner ready to send
const whatsappNumber = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  return digits.length >= 11 && digits.length <= 15 ? digits : "";
};
function CustomerWhatsapp({ order, site }) {
  const number = whatsappNumber(order.customer.phone);
  if (!number) return null;
  const from = site ? `${site.owner ? `${site.owner} from ` : ""}${site.name}` : "your shop";
  const text = `Hello ${order.customer.name || "there"}, this is ${from}. I'm writing about your order #${order.id} placed on ${dateText(order.createdAt)}.`;
  return (
    <a className="mo-whatsapp" href={`https://wa.me/${number}?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer" title={`WhatsApp ${order.customer.name || "the customer"}`} aria-label={`WhatsApp ${order.customer.name || "the customer"} about order ${order.id}`}>
      <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.3-.4.7-1.4.1-.2 0-.3 0-.5l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.3-.9.9-.9 2.2s.9 2.5 1 2.7c.1.2 1.8 2.8 4.4 3.9 1.6.7 2.3.8 3.1.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2l-.6-.3Z" /></svg>
    </a>
  );
}
const paymentNote = (o) => {
  if (awaitingManualUpi(o)) return "Awaiting your check · Manual UPI";
  if (!isGatewayOrder(o)) return PAYMENT_STATUS[o.paymentStatus] || "";
  if (isDemoPayment(o)) return `Demo payment · ${o.paymentStatus === "paid" ? "no money received" : PAYMENT_STATUS[o.paymentStatus] || ""}`;
  return `${PAYMENT_STATUS[o.paymentStatus] || ""} · Razorpay${o.paymentStatus === "pending" ? ", confirmed automatically" : ""}`;
};
const FINAL_STATUSES = ["Cancelled", "Refunded"]; // the server freezes these orders
const ALL_STATUSES = "All statuses";
const ALL_PAYMENTS = "All payments";
const SHOW_DEMO_BUTTON = process.env.REACT_APP_ENABLE_DEMO_ORDERS === "true"; // the backend refuses demo orders in production

const orderAddress = (c) => [c.address, c.address2, c.city, c.state, c.pincode, c.country].filter(Boolean).join(", ");
const itemCount = (o) => o.items.reduce((n, x) => n + x.qty, 0);
const statusClass = (status) => `status-${status.toLowerCase().replace(/[^a-z]+/g, "-")}`;
// The colour family of a status: waiting (amber), on the way (blue), done (green) or stopped (red)
// An order can be booked with a courier until it has an AWB, is delivered, cancelled or refunded
const canShip = (o) => !(o.shipment && o.shipment.awb) && !FINAL_STATUSES.includes(o.status) && o.status !== "Delivered";
// The statuses an order can be set to: never back to Pending (the server refuses), so it is listed only while Pending
const statusChoices = (current) => (current === "Pending" ? STATUSES : STATUSES.filter((x) => x !== "Pending"));
const statusTone = (status) =>
  FINAL_STATUSES.includes(status) ? "tone-red" : status === "Delivered" ? "tone-green" : ["Shipped", "Out for delivery"].includes(status) ? "tone-blue" : "tone-amber";

// Orders for one website: filter, search, edit status and payment, export CSV. Its own page at
// /admin/sites/:siteId/orders, or inside the Order management tab (embedded, with that tab's website picker on top).
export default function ManageOrders({ siteId: siteProp, embedded = false, sitePicker = null }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const routeParams = useParams();
  const siteId = siteProp || routeParams.siteId;
  const { orders, stats, total, page, pages, loading, error } = useSelector((state) => state.order);
  const user = useSelector(selectUser);
  const [site, setSite] = useState(null);
  const [siteError, setSiteError] = useState("");

  const [search, setSearch] = useState("");
  // Quick links (Overview -> Order management) open the list already filtered: ?status=Packed, ?payment=Manual UPI,
  // ?from=2026-10-25&to=2026-10-25
  const [params] = useSearchParams();
  const [status, setStatus] = useState(() => (STATUSES.includes(params.get("status")) ? params.get("status") : ALL_STATUSES));
  const [payment, setPayment] = useState(() => (PAYMENT_METHODS.includes(params.get("payment")) ? params.get("payment") : ALL_PAYMENTS));
  const [sort, setSort] = useState("newest");
  const [from, setFrom] = useState(() => params.get("from") || "");
  const [to, setTo] = useState(() => params.get("to") || "");
  const [pageNumber, setPageNumber] = useState(1);
  const [details, setDetails] = useState(null);
  const [editing, setEditing] = useState(null);
  const [shipping, setShipping] = useState(null); // order being booked with a courier
  const [tracking, setTracking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false); // phones: filters open in a sheet from the bottom
  const debouncedSearch = useDebounced(search);

  // The filters exactly as the API understands them
  const filters = {
    search: debouncedSearch,
    status: status === ALL_STATUSES ? "" : status,
    payment: payment === ALL_PAYMENTS ? "" : payment,
    from,
    to,
    sort,
  };
  const filterKey = JSON.stringify(filters);

  const load = useCallback(() => {
    dispatch(fetchOrders({ siteId, params: { ...JSON.parse(filterKey), page: pageNumber, limit: 25 } }));
  }, [dispatch, siteId, filterKey, pageNumber]);

  useEffect(() => { load(); }, [load]);

  // An order accepted or rejected on the new order alarm, or a new order placed: the list, and the order open in
  // details, show it without a refresh
  const openId = details && details.id;
  useEffect(() => onOrdersChanged(() => {
    load();
    if (openId) getOrderById(siteId, openId).then(({ data }) => setDetails((now) => (now && now.id === data.order.id ? data.order : now))).catch(() => {});
  }), [load, siteId, openId]);

  // ?order=<orderId> (View on the new order alarm, an alert): opens that order's details over the full list. The list
  // itself is not filtered to it, so the store's other orders (the same shopper's too) stay in view.
  const linkedOrder = params.get("order");
  useEffect(() => {
    if (!linkedOrder) return undefined;
    let live = true;
    getOrderById(siteId, linkedOrder)
      .then(({ data }) => live && setDetails(data.order))
      .catch((err) => live && notify(errorMessage(err), "error"));
    return () => { live = false; };
  }, [siteId, linkedOrder]);

  useEffect(() => {
    getSite(siteId)
      .then(({ data }) => setSite(data.site))
      .catch((err) => setSiteError(errorMessage(err)));
  }, [siteId]);

  // While a dialog is open the page behind it doesn't scroll, and Escape closes it (the filter sheet does this itself)
  const overlayOpen = Boolean(details || editing || shipping);
  useEffect(() => {
    if (!overlayOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setDetails(null); setEditing(null);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [overlayOpen]);

  const change = (setter) => (e) => { setter(e.target.value); setPageNumber(1); };
  const pick = (setter, value) => { setter(value); setPageNumber(1); };
  const activeFilters = [status !== ALL_STATUSES, payment !== ALL_PAYMENTS, from, to, sort !== "newest"].filter(Boolean).length;
  const resetFilters = () => {
    setSearch(""); setStatus(ALL_STATUSES); setPayment(ALL_PAYMENTS); setFrom(""); setTo(""); setSort("newest"); setPageNumber(1);
  };

  const exportCsv = async (format = "csv") => {
    try {
      await exportOrdersCsv(siteId, filters, format);
    } catch (err) {
      // the error body of a blob download is a blob too
      let text = errorMessage(err);
      if (err.response && err.response.data instanceof Blob) {
        try { text = JSON.parse(await err.response.data.text()).message || text; } catch { /* keep the default */ }
      }
      notify(text, "error");
    }
  };

  const addDemo = async () => {
    try {
      const { data } = await addDemoOrders(siteId, 50);
      notify(data.message);
      load();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };

  const updateStatus = async (order, value) => {
    try {
      const { data } = await updateOrder(siteId, order.id, { status: value });
      dispatch(updateSingleOrder(data.order));
      notify(`Order #${order.id} marked as ${value}`);
      load(); // the cards at the top change with the status
    } catch (err) {
      notify(errorMessage(err), "error");
      load(); // put the dropdown back to what the server has
    }
  };

  // A new order: accept (Confirmed) or reject (cancelled, stock back). The alarm stops once every order is answered.
  const answer = async (order, accept) => {
    try {
      const { data } = await (accept ? acceptOrder(siteId, order.id) : rejectOrder(siteId, order.id));
      dispatch(updateSingleOrder(data.order));
      setDetails((current) => (current && current.id === order.id ? data.order : current));
      notify(data.message);
      ordersChanged(); // reloads this list, and the alarm and badges drop the order
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };
  const answerButtons = (o) => o.awaitingAnswer && (
    <>
      <button className="mo-accept" onClick={() => answer(o, true)} title="Accept this new order">✓ Accept</button>
      <button className="mo-reject" onClick={() => answer(o, false)} title="Reject: the order is cancelled">✕ Reject</button>
    </>
  );

  // Manual UPI: once the money shows in the owner's UPI or bank app
  const markPaid = async (order) => {
    try {
      const { data } = await updateOrder(siteId, order.id, { paymentStatus: "paid" });
      dispatch(updateSingleOrder(data.order));
      setDetails((current) => (current && current.id === order.id ? data.order : current));
      notify(`Order #${order.id} marked as paid`);
      load();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };

  const startEdit = (order) =>
    setEditing({
      id: order.id,
      status: order.status,
      payment: order.payment,
      paymentStatus: order.paymentStatus,
      trackingNumber: order.trackingNumber || "",
      notes: order.notes || "",
      name: order.customer.name || "",
      email: order.customer.email || "",
      phone: order.customer.phone || "",
      address: order.customer.address || "",
    });
  const setField = (key) => (e) => setEditing((o) => ({ ...o, [key]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        status: editing.status,
        payment: editing.payment,
        paymentStatus: editing.paymentStatus,
        trackingNumber: editing.trackingNumber,
        notes: editing.notes,
        customer: { name: editing.name, email: editing.email, phone: editing.phone, address: editing.address },
      };
      const { data } = await updateOrder(siteId, editing.id, payload);
      dispatch(updateSingleOrder(data.order));
      setEditing(null);
      notify("Order updated successfully.");
      load();
    } catch (err) {
      notify(errorMessage(err), "error"); // e.g. "The order was changed by someone else. Reload and try again."
    } finally {
      setSaving(false);
    }
  };

  const editingFinal = editing && FINAL_STATUSES.includes(orders.find((o) => o.id === editing.id)?.status);

  return (
    <div className={`manage-orders-shell${embedded ? " is-embedded" : ""}`}>
      {!embedded && (
      <header className="mo-top">
        <button className="mo-back" onClick={() => navigate("/admin")} aria-label="Back to dashboard"><span aria-hidden="true">←</span><em>Dashboard</em></button>
        <div className="mo-brand">
          <span aria-hidden="true">📦</span>
          <div><b>Order management</b><small>{site ? site.name : siteId}</small></div>
        </div>
        <div className="mo-export-group">
          <AlertBell className="on-dark" />
          <button className="mo-primary mo-export" onClick={() => exportCsv("xlsx")}><span aria-hidden="true">⇩</span><em>Excel</em></button>
          <button className="mo-primary mo-export mo-export-csv" onClick={() => exportCsv("csv")}><em>CSV</em></button>
        </div>
      </header>
      )}
      <main className="mo-content">
        {sitePicker}
        <div className="mo-heading">
          <div><p className="mo-eyebrow">STORE OPERATIONS</p><h1>Manage orders</h1><p>Search orders, update fulfilment, edit customer details and track deliveries.</p></div>
          <div className="mo-heading-actions">
            {SHOW_DEMO_BUTTON && <button className="mo-secondary" onClick={addDemo}>＋ Add 50 demo orders</button>}
            <button className="mo-secondary mo-reset-top" onClick={resetFilters}>Reset filters</button>
            {embedded && <button className="mo-secondary" onClick={() => exportCsv("xlsx")}>⇩ Excel</button>}
            {embedded && <button className="mo-secondary" onClick={() => exportCsv("csv")}>⇩ CSV</button>}
          </div>
        </div>

        {siteError && <div className="admin-error">{siteError}</div>}
        <section className="mo-stats">
          <article className="tone-slate"><i aria-hidden="true">🧾</i><span>Total orders</span><strong>{stats.all}</strong><small>All recorded orders</small></article>
          <article className="tone-amber"><i aria-hidden="true">⏳</i><span>Needs processing</span><strong>{stats.pending}</strong><small>Pending, confirmed, processing or packed</small></article>
          <article className="tone-blue"><i aria-hidden="true">🚚</i><span>In transit</span><strong>{stats.shipped}</strong><small>Shipped / out for delivery</small></article>
          <article className="tone-green"><i aria-hidden="true">✅</i><span>Delivered</span><strong>{stats.delivered}</strong><small>Successfully completed</small></article>
          <article className="tone-violet"><i aria-hidden="true">₹</i><span>Order value</span><strong>{money(stats.revenue)}</strong><small>Excludes cancelled/refunded</small></article>
        </section>

        {/* Customers' return and refund requests, and the store's return policy */}
        <ReturnsPanel siteId={siteId} notify={notify} onChanged={load} />

        <section className="mo-panel">
          <div className="mo-filters">
            <label className="mo-search"><span aria-hidden="true">⌕</span><input value={search} onChange={change(setSearch)} placeholder="Search order ID, customer, email, phone…" /></label>
            <FilterButton count={activeFilters} onClick={() => setFiltersOpen(true)} />
            <div className="mo-inline-filters">
              <select value={status} onChange={change(setStatus)} aria-label="Filter by status"><option>{ALL_STATUSES}</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
              <select value={payment} onChange={change(setPayment)} aria-label="Filter by payment"><option>{ALL_PAYMENTS}</option>{PAYMENT_METHODS.map((p) => <option key={p}>{p}</option>)}</select>
              <label className="mo-date">From <input type="date" value={from} onChange={change(setFrom)} /></label>
              <label className="mo-date">To <input type="date" value={to} onChange={change(setTo)} /></label>
              <select value={sort} onChange={change(setSort)} aria-label="Sort orders">
                <option value="newest">Newest first</option><option value="oldest">Oldest first</option>
                <option value="total-high">Highest total</option><option value="total-low">Lowest total</option>
              </select>
            </div>
          </div>
          <div className="mo-table-meta"><span><b>{total}</b> orders match</span><span>Page {page} of {pages}</span></div>
          {error && <div className="admin-error">{error}</div>}

          {/* Wide screens: a table. Phones: one card per order (the CSS shows one or the other). */}
          <DataTable className="mo-table" wrapClassName="mo-table-wrap">
              <thead><tr><th>ORDER</th><th>CUSTOMER</th><th>DATE</th><th>ITEMS</th><th>TOTAL</th><th>PAYMENT</th><th>STATUS</th><th>ACTIONS</th></tr></thead>
              <tbody>
                {orders.map((o) => {
                  const count = itemCount(o);
                  return (
                    <tr key={o.id}>
                      <td><b className="mo-order-id">#{o.id}</b>{o.awaitingAnswer && <em className="mo-new">NEW</em>}{o.returnRequest && o.returnRequest.status === "pending" && <em className="mo-new mo-return-tag">RETURN</em>}<small>{o.trackingNumber ? `Tracking: ${o.trackingNumber}` : "Online order"}</small></td>
                      <td><div className="mo-customer-cell"><div><b>{o.customer.name || "Guest customer"}</b><small>{o.customer.email || o.customer.phone || "Contact not provided"}</small></div><CustomerWhatsapp order={o} site={site} /></div></td>
                      <td>{dateText(o.createdAt)}</td>
                      <td>{count} item{count === 1 ? "" : "s"}<small>{o.items.slice(0, 2).map((x) => x.name).join(", ")}{o.items.length > 2 ? "…" : ""}</small></td>
                      <td><b className="mo-amount">{money(o.total)}</b></td>
                      <td><span className="mo-payment">{o.payment}</span><small>{paymentNote(o)}</small></td>
                      <td>
                        <select className={`mo-status ${statusClass(o.status)}`} value={o.status} disabled={FINAL_STATUSES.includes(o.status)}
                          onChange={(e) => updateStatus(o, e.target.value)} aria-label={`Update status for order ${o.id}`}>
                          {statusChoices(o.status).map((x) => <option key={x}>{x}</option>)}
                        </select>
                      </td>
                      <td><div className="mo-actions">{answerButtons(o)}<button onClick={() => setDetails(o)} title="View order details">View</button>{awaitingManualUpi(o) && <button className="mo-mark-paid" onClick={() => markPaid(o)} title="The UPI payment is in your account">Mark paid</button>}<button onClick={() => startEdit(o)} title="Edit order">Edit</button>{canShip(o) && <button className="mo-ship-btn" onClick={() => setShipping(o)} title="Book a courier">🚚 Ship</button>}</div></td>
                    </tr>
                  );
                })}
                {!orders.length && (
                  <tr><td colSpan="8" className="mo-empty">{loading ? <Loader label="Loading orders" /> : <><span>📦</span><b>No orders found</b><small>{stats.all ? "Try changing the search or filters." : "Orders placed by customers will appear here."}</small></>}</td></tr>
                )}
              </tbody>
          </DataTable>

          <div className="mo-cards">
            {orders.map((o) => {
              const count = itemCount(o);
              const name = o.customer.name || "Guest customer";
              return (
                <article className={`mo-card ${statusTone(o.status)}`} key={o.id}>
                  <div className="mo-card-top">
                    <div><b className="mo-order-id">#{o.id}</b><small>{dateText(o.createdAt)}</small></div>
                    <span className="mo-chip">{o.status}</span>
                  </div>
                  <div className="mo-card-customer">
                    <span className="mo-avatar" aria-hidden="true">{name.charAt(0).toUpperCase()}</span>
                    <div><b>{name}</b><small>{o.customer.phone || o.customer.email || "Contact not provided"}</small></div>
                    <CustomerWhatsapp order={o} site={site} />
                  </div>
                  <div className="mo-card-meta">
                    <div><span>Items</span><b>{count}</b><small>{o.items.slice(0, 1).map((x) => x.name).join("")}{o.items.length > 1 ? ` +${o.items.length - 1}` : ""}</small></div>
                    <div><span>Total</span><b className="mo-amount">{money(o.total)}</b></div>
                    <div><span>Payment</span><b>{o.payment}</b><small>{paymentNote(o)}</small></div>
                  </div>
                  {/* Gift orders: the date to deliver on, and whether it has a message or photos to make */}
                  {(o.deliveryDate || o.items.some((x) => x.text || (x.photos && x.photos.length))) && (
                    <div className="mo-card-gift">
                      {o.deliveryDate && <span>📅 Deliver {new Date(`${o.deliveryDate}T00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>}
                      {o.items.some((x) => x.text || (x.photos && x.photos.length)) && <span>✍️ Personalised</span>}
                    </div>
                  )}
                  <label className="mo-card-status">
                    <span>Order status</span>
                    <select className={`mo-status ${statusClass(o.status)}`} value={o.status} disabled={FINAL_STATUSES.includes(o.status)}
                      onChange={(e) => updateStatus(o, e.target.value)} aria-label={`Update status for order ${o.id}`}>
                      {statusChoices(o.status).map((x) => <option key={x}>{x}</option>)}
                    </select>
                  </label>
                  <div className="mo-card-actions">
                    {answerButtons(o)}
                    <button className="mo-view" onClick={() => setDetails(o)}>View details</button>
                    <button className="mo-edit" onClick={() => startEdit(o)}>Edit order</button>
                    {canShip(o) && <button className="mo-ship-btn mo-card-ship" onClick={() => setShipping(o)}>🚚 Ship with courier</button>}
                  </div>
                </article>
              );
            })}
            {!orders.length && (
              <div className="mo-card-empty">{loading ? <Loader label="Loading orders" /> : <><span>📦</span><b>No orders found</b><small>{stats.all ? "Try changing the search or filters." : "Orders placed by customers will appear here."}</small></>}</div>
            )}
          </div>
          <Pager page={page} pages={pages} total={total} noun="orders" onPage={setPageNumber} />
        </section>
      </main>

      {filtersOpen && (
        <FilterSheet title="Filter orders" resultLabel={`Show ${total} order${total === 1 ? "" : "s"}`} onClose={() => setFiltersOpen(false)} onReset={resetFilters}>
          <FilterChips label="Order status" value={status} onChange={(x) => pick(setStatus, x)}
            options={[{ value: ALL_STATUSES, label: "All" }, ...STATUSES.map((x) => ({ value: x, label: x, tone: statusTone(x).replace("tone-", "") }))]} />
          <FilterField label="Payment">
            <select value={payment} onChange={change(setPayment)}><option>{ALL_PAYMENTS}</option>{PAYMENT_METHODS.map((p) => <option key={p}>{p}</option>)}</select>
          </FilterField>
          <div className="filter-dates">
            <FilterField label="From"><input type="date" value={from} onChange={change(setFrom)} /></FilterField>
            <FilterField label="To"><input type="date" value={to} onChange={change(setTo)} /></FilterField>
          </div>
          <FilterField label="Sort by">
            <select value={sort} onChange={change(setSort)}>
              <option value="newest">Newest first</option><option value="oldest">Oldest first</option>
              <option value="total-high">Highest total</option><option value="total-low">Lowest total</option>
            </select>
          </FilterField>
        </FilterSheet>
      )}

      {details && (
        <div className="mo-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setDetails(null); }}>
          <section className="mo-modal mo-details" role="dialog" aria-modal="true" aria-label={`Order ${details.id}`}>
            <div className={`mo-modal-hero ${statusTone(details.status)}`}>
              <div>
                <small>ORDER DETAILS</small>
                <h2>#{details.id}</h2>
                <span className="mo-hero-date">Placed {dateText(details.createdAt)}</span>
              </div>
              <span className="mo-chip">{details.status}</span>
              <button className="mo-modal-close" onClick={() => setDetails(null)} aria-label="Close">✕</button>
            </div>
            <div className="mo-modal-body">
              <div className="mo-info-grid">
                <div className="mo-info-card">
                  <i aria-hidden="true">👤</i>
                  <div><span>Customer</span><b>{details.customer.name || "Guest customer"}</b>
                    {details.customer.phone && <span className="mo-contact-line"><a href={`tel:${details.customer.phone}`}>{details.customer.phone}</a><CustomerWhatsapp order={details} site={site} /></span>}
                    {details.customer.email && <a href={`mailto:${details.customer.email}`}>{details.customer.email}</a>}
                  </div>
                </div>
                <div className="mo-info-card">
                  <i aria-hidden="true">💳</i>
                  <div><span>Payment</span><b>{details.payment}</b><em className={`mo-pay-chip pay-${details.paymentStatus}`}>{PAYMENT_STATUS[details.paymentStatus]}</em>
                    {details.payment === "Manual UPI" && (
                      <small>
                        Paid straight to {details.manualUpi ? details.manualUpi.upiId : "your UPI ID"}{awaitingManualUpi(details) ? ". Check that the money is in your UPI or bank app (the shopper's WhatsApp message has the UTR), then mark it paid." : ""}
                      </small>
                    )}
                    {awaitingManualUpi(details) && <button className="mo-mark-paid" onClick={() => markPaid(details)}>✓ Mark as paid</button>}
                    {isGatewayOrder(details) && (
                      <small>
                        {isDemoPayment(details) ? "Demo payment: no real money (demo store or free trial)" : "Through Razorpay"}
                        {details.gateway.utr ? ` · UTR ${details.gateway.utr}` : ""}{details.gateway.paymentId ? ` · ${details.gateway.paymentId}` : ""}
                        {details.paymentStatus === "pending" ? " · confirmed automatically when the shopper pays" : ""}
                      </small>
                    )}
                  </div>
                </div>
                <div className="mo-info-card mo-wide">
                  <i aria-hidden="true">📍</i>
                  <div><span>Delivery address</span><b>{orderAddress(details.customer) || "No address saved"}</b>
                    {details.trackingNumber && <small>Tracking: {details.trackingNumber}</small>}
                  </div>
                </div>
                {/* What the shopper wrote at checkout, e.g. the name or message to put on a customised gift */}
                {details.shopperNote && (
                  <div className="mo-info-card mo-wide mo-shopper-note">
                    <i aria-hidden="true">✍️</i>
                    <div><span>Note from the customer</span><b>{details.shopperNote}</b></div>
                  </div>
                )}
                {details.notes && (
                  <div className="mo-info-card mo-wide mo-note-card">
                    <i aria-hidden="true">📝</i>
                    <div><span>Notes</span><b>{details.notes}</b></div>
                  </div>
                )}
              </div>
              {details.shipment && details.shipment.awb ? (
                <div className="mo-shipment">
                  <div className="mo-shipment-head">
                    <div><span>Shipped with</span><b>{details.shipment.courier || "Courier"}</b><small>AWB {details.shipment.awb}{details.shipment.status ? ` · ${details.shipment.status}` : ""}</small></div>
                    <button className="mo-secondary" disabled={tracking} onClick={async () => {
                      setTracking(true);
                      try {
                        const { data } = await trackOrder(siteId, details.id);
                        setDetails(data.order);
                        dispatch(updateSingleOrder(data.order));
                        notify(data.message);
                      } catch (err) {
                        notify(errorMessage(err), "error");
                      } finally {
                        setTracking(false);
                      }
                    }}>{tracking ? <><Spinner /> Checking</> : "↻ Refresh tracking"}</button>
                  </div>
                  <div className="mo-shipment-links">
                    {details.shipment.trackUrl && <a href={details.shipment.trackUrl} target="_blank" rel="noopener noreferrer">Track shipment ↗</a>}
                    {details.shipment.labelUrl && <a href={details.shipment.labelUrl} target="_blank" rel="noopener noreferrer">Print label ↗</a>}
                  </div>
                  {details.shipment.events && details.shipment.events.length > 0 && (
                    <ol className="mo-shipment-events">
                      {details.shipment.events.slice(0, 5).map((e, i) => <li key={i}><b>{e.activity}</b><small>{e.location}{e.at ? ` · ${e.at}` : ""}</small></li>)}
                    </ol>
                  )}
                </div>
              ) : canShip(details) && (
                <button className="mo-ship-cta" onClick={() => { setShipping(details); setDetails(null); }}>🚚 Ship with courier <small>Delhivery, Bluedart, Ekart and more via Shiprocket</small></button>
              )}
              {/* Gift options the shopper picked at checkout (gift stores) */}
              {(details.deliveryDate || details.giftWrap || details.giftCard) && (
                <div className="mo-gifts">
                  {details.deliveryDate && <div><span>📅 Deliver on</span><b>{new Date(`${details.deliveryDate}T00:00`).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</b></div>}
                  {details.giftWrap && <div><span>🎀 Gift wrap</span><b>Yes, wrap it</b></div>}
                  {details.giftCard && <div><span>💌 Greeting card</span><b>{details.giftCard.message ? `“${details.giftCard.message}”` : "Add a card (no message)"}</b></div>}
                </div>
              )}
              <h3>Items <span>{itemCount(details)}</span></h3>
              <div className="mo-items">
                {details.items.map((it, index) => (
                  <div className="mo-item" key={`${it.id}-${index}`}>
                    <span className="mo-item-qty">{it.qty}×</span>
                    <span className="mo-item-name">{it.name}<small>{[it.size && (/^\d/.test(it.size) || it.size.length > 4 ? it.size : `Size ${it.size}`), it.color, `${money(it.price)} each`].filter(Boolean).join(" · ")}</small>
                      {/* Gift personalisation: what to print, and the shopper's photos to open or download for making it */}
                      {it.text && <span className="mo-item-personal">✍️ “{it.text}”</span>}
                      {Array.isArray(it.photos) && it.photos.length > 0 && (
                        <span className="mo-item-photos">
                          {it.photos.map((url, i) => (
                            <a key={url} href={url} target="_blank" rel="noopener noreferrer" download title={`Open photo ${i + 1}`}><img src={url} alt={`Customer photo ${i + 1}`} /></a>
                          ))}
                        </span>
                      )}
                    </span>
                    <b>{money(it.price * it.qty)}</b>
                  </div>
                ))}
              </div>
              <div className="mo-summary">
                <div><span>Items total</span><b>{money(details.items.reduce((n, it) => n + it.price * it.qty, 0))}</b></div>
                {details.discount > 0 && <div className="mo-discount"><span>Coupon{details.coupon && details.coupon.code ? ` (${details.coupon.code})` : ""}</span><b>−{money(details.discount)}</b></div>}
                {details.extras > 0 && <div><span>Gift wrap &amp; card</span><b>{money(details.extras)}</b></div>}
                <div><span>Tax</span><b>{money(details.tax)}</b></div>
                <div><span>Shipping</span><b>{money(details.ship)}</b></div>
                <div className="mo-total"><span>Order total</span><strong>{money(details.total)}</strong></div>
              </div>
            </div>
            <div className="mo-modal-actions">
              {answerButtons(details)}
              <button className="mo-secondary" onClick={() => { startEdit(details); setDetails(null); }}>✎ Edit order</button>
              <button className="mo-primary" onClick={() => setDetails(null)}>Done</button>
            </div>
          </section>
        </div>
      )}

      {shipping && (
        <ShipModal siteId={siteId} order={shipping} notify={notify} onClose={() => setShipping(null)}
          onBooked={(order) => { setShipping(null); dispatch(updateSingleOrder(order)); setDetails(order); load(); }} />
      )}

      {editing && (
        <div className="mo-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setEditing(null); }}>
          <form className="mo-modal mo-edit-modal" onSubmit={save} aria-label={`Edit order ${editing.id}`}>
            <div className={`mo-modal-hero ${statusTone(editing.status)}`}>
              <div><small>UPDATE ORDER</small><h2>Edit #{editing.id}</h2><span className="mo-hero-date">Changes are saved to this order only</span></div>
              <button type="button" className="mo-modal-close" onClick={() => setEditing(null)} aria-label="Close">✕</button>
            </div>
            <div className="mo-modal-body">
              <fieldset className="mo-edit-section">
                <legend><i aria-hidden="true">🚚</i> Order &amp; payment</legend>
                <div className="mo-edit-grid">
                  <label>Status<select value={editing.status} disabled={editingFinal} onChange={setField("status")}>{statusChoices(editing.status).map((x) => <option key={x}>{x}</option>)}</select></label>
                  <label>Tracking number<input value={editing.trackingNumber} maxLength={60} onChange={setField("trackingNumber")} placeholder="Courier tracking ID" /></label>
                  <label>Payment method<select value={editing.payment} onChange={setField("payment")}>{PAYMENT_METHODS.map((x) => <option key={x}>{x}</option>)}</select></label>
                  <label>Payment status<select value={editing.paymentStatus} onChange={setField("paymentStatus")}>{Object.entries(PAYMENT_STATUS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                </div>
                {editingFinal && <p className="mo-edit-hint">Cancelled and refunded orders can't change status.</p>}
              </fieldset>
              <fieldset className="mo-edit-section">
                <legend><i aria-hidden="true">👤</i> Customer</legend>
                <div className="mo-edit-grid">
                  <label className="mo-wide">Customer name<input value={editing.name} onChange={setField("name")} /></label>
                  <label>Phone<input type="tel" value={editing.phone} onChange={setField("phone")} /></label>
                  <label>Email<input type="email" value={editing.email} onChange={setField("email")} /></label>
                </div>
              </fieldset>
              <fieldset className="mo-edit-section">
                <legend><i aria-hidden="true">📍</i> Delivery &amp; notes</legend>
                <div className="mo-edit-grid">
                  <label className="mo-wide">Delivery address<textarea rows="3" value={editing.address} onChange={setField("address")} /></label>
                  <label className="mo-wide">Order notes<textarea rows="3" value={editing.notes} maxLength={1000} onChange={setField("notes")} placeholder="Internal fulfilment notes" /></label>
                </div>
              </fieldset>
            </div>
            <div className="mo-modal-actions">
              <button type="button" className="mo-secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button className="mo-primary" type="submit" disabled={saving}>{saving ? <><Spinner /> Saving</> : "Save changes"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
